import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Badge, Button } from '@/ui/primitives'
import { DataTable, type Coloana } from '@/ui/data-table'
import { NewMenu } from '@/ui/new-menu'
import { lei } from '@/lib/money'

type Oferta = {
  id: number; number: string | null; contact_name: string; destination: string | null
  hotel: string | null; begin_date: string | null; end_date: string | null
  total_value: number; currency: string; margin: number; status: string | null
  valid_until: string | null; created_at: string | null
  converted_to_reservation_id: number | null
}

const zi = (s: string | null) => (s ? new Date(s).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: '2-digit' }) : '—')

const TON: Record<string, 'good' | 'warn' | 'bad' | 'neutral'> = {
  accepted: 'good', sent: 'warn', draft: 'neutral', rejected: 'bad', expired: 'bad', converted: 'good',
}

export function Offers() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const { data, isPending } = useQuery({
    queryKey: ['oferte'], queryFn: () => api.get<{ offers: Oferta[]; total: number }>('/offers/', { limit: 200 }),
  })

  const converteste = useMutation({
    mutationFn: (id: number) => api.post<{ reservation_id?: number }>(`/offers/${id}/convert`),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['oferte'] })
      qc.invalidateQueries({ queryKey: ['rezervari'] })
      if (r?.reservation_id) nav(`/rezervari/${r.reservation_id}`)
    },
  })

  const coloane: Coloana<Oferta>[] = [
    { cheie: 'nr', antet: 'Oferta', sortabil: true, valoare: (o) => o.number ?? '',
      randare: (o) => <span className="font-medium">{o.number ?? `#${o.id}`}</span> },
    { cheie: 'client', antet: 'Client', sortabil: true, valoare: (o) => o.contact_name,
      randare: (o) => <span className="truncate block max-w-[220px]">{o.contact_name}</span> },
    { cheie: 'dest', antet: 'Destinatie', sortabil: true, valoare: (o) => o.destination ?? '',
      randare: (o) => <span className="text-muted">{o.destination || o.hotel || '—'}</span> },
    { cheie: 'plecare', antet: 'Plecare', sortabil: true, valoare: (o) => o.begin_date ?? '',
      randare: (o) => <span className="tnum">{zi(o.begin_date)}</span> },
    { cheie: 'valoare', antet: 'Valoare', sortabil: true, aliniat: 'dreapta', valoare: (o) => o.total_value,
      randare: (o) => <span className="tnum">{lei(o.total_value)} <span className="text-[11.5px] text-muted">{o.currency}</span></span> },
    { cheie: 'marja', antet: 'Marja', sortabil: true, aliniat: 'dreapta', valoare: (o) => o.margin,
      randare: (o) => <span className="tnum text-muted">{lei(o.margin)}</span> },
    { cheie: 'status', antet: 'Stare', sortabil: true, valoare: (o) => o.status ?? '',
      randare: (o) => <Badge tone={TON[o.status ?? ''] ?? 'neutral'}>{o.status ?? '—'}</Badge> },
    { cheie: 'actiuni', antet: '', randare: (o) => (
      o.converted_to_reservation_id
        ? <button className="text-[13px] text-accent-ink hover:underline"
            onClick={(e) => { e.stopPropagation(); nav(`/rezervari/${o.converted_to_reservation_id}`) }}>
            vezi rezervarea
          </button>
        : <button className="text-[13px] text-accent-ink hover:underline disabled:opacity-40"
            disabled={converteste.isPending}
            onClick={(e) => { e.stopPropagation(); converteste.mutate(o.id) }}>
            fa rezervare
          </button>
    ) },
  ]

  return (
    <>
      <PageHeader title="Oferte" count={data ? `${data.total} oferte` : undefined}
        actions={<><Button onClick={() => nav('/nou/oferta')}>Oferta noua</Button><NewMenu /></>} />
      <div className="px-8 py-6">
        <DataTable
          randuri={data?.offers ?? []} coloane={coloane} cheie={(o) => o.id} incarca={isPending}
          sortImplicit={{ cheie: 'plecare', sens: 'asc' }}
          cauta={(o, t) => `${o.number ?? ''} ${o.contact_name} ${o.destination ?? ''} ${o.hotel ?? ''}`.toLowerCase().includes(t)}
          placeholderCautare="Cauta oferta, client, destinatie…"
          gol={{ titlu: 'Nicio oferta', indiciu: 'Ofertele trimise clientilor apar aici.' }}
        />
      </div>
    </>
  )
}
